import { useState, useEffect } from "react";
import { ShieldCheck, ShieldAlert, Star, X, GitCompare, Check, ChevronRight, Ticket, Loader2, Timer } from "lucide-react";
import { fmtMoney, errorMessage } from "@/api";
import { toast } from "sonner";

function Countdown({ venceAt }) {
  const [restante, setRestante] = useState("");
  useEffect(() => {
    if (!venceAt) return;
    const tick = () => {
      const diff = new Date(venceAt).getTime() - Date.now();
      if (diff <= 0) { setRestante("vencido"); return; }
      const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
      setRestante(d > 0 ? `${d}d ${h}h ${m}m` : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [venceAt]);
  if (!venceAt || !restante) return null;
  return (
    <span className="inline-flex items-center gap-1 font-bold text-amber-700" data-testid="cupon-countdown">
      <Timer className="w-3.5 h-3.5" /> {restante === "vencido" ? "Oferta vencida" : `Te quedan ${restante}`}
    </span>
  );
}

function CuponBox({ cupon, onAplicar, onQuitar }) {
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [cargando, setCargando] = useState(false);

  const aplicar = async () => {
    if (!codigo.trim()) return;
    setCargando(true);
    try {
      const data = await onAplicar(codigo.trim());
      toast.success(data?.cupon?.frase || "¡Cupón aplicado!");
    } catch (err) {
      toast.error(errorMessage(err, "Cupón inválido o vencido"));
    } finally { setCargando(false); }
  };

  if (cupon) {
    return (
      <div className="rounded-[20px] border-2 border-amber-300 bg-amber-50 p-4 mb-4" data-testid="cupon-aplicado">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading font-bold text-amber-900 flex items-center gap-1.5"><Ticket className="w-4 h-4" /> {cupon.frase || `Cupón ${cupon.codigo} aplicado`}</p>
            <p className="text-sm text-amber-800 mt-0.5">Tenés un <b>{cupon.bonificacion}% de descuento</b> aplicado a todos los precios.</p>
            {cupon.vence_at && (
              <p className="text-xs mt-1.5 text-amber-800" data-testid="cupon-vencimiento">
                Válido hasta el <b>{new Date(cupon.vence_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })} hs</b> · <Countdown venceAt={cupon.vence_at} />
              </p>
            )}
          </div>
          <button type="button" data-testid="cupon-quitar" onClick={onQuitar} className="shrink-0 text-xs text-amber-700 underline underline-offset-2">Quitar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="cupon-box">
      {!abierto ? (
        <button type="button" data-testid="cupon-abrir" onClick={() => setAbierto(true)}
          className="flex items-center gap-2 text-sm font-semibold text-emerald-700 underline underline-offset-2">
          <Ticket className="w-4 h-4" /> ¿Tenés un cupón de descuento?
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input data-testid="cupon-input" value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") aplicar(); }} placeholder="Ingresá tu código"
            className="flex-1 min-w-0 h-11 rounded-[14px] border-2 border-slate-200 px-4 text-sm uppercase focus:border-emerald-500 outline-none" />
          <button type="button" data-testid="cupon-aplicar" onClick={aplicar} disabled={cargando || !codigo.trim()}
            className="shrink-0 h-11 px-4 rounded-[14px] bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-heading font-semibold text-sm flex items-center gap-1.5">
            {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
          </button>
        </div>
      )}
    </div>
  );
}

function descPorDefecto(c) {
  const d = (c.descripcion || "").toUpperCase();
  if (c.tipo === "robo") {
    return d.includes("PREMIUM")
      ? "La protección más completa: robo sin franquicia, incendio, destrucción total y reposición 0km."
      : "Además de la Responsabilidad Civil, cubre la pérdida total por robo o hurto y por incendio de tu moto.";
  }
  return d.includes("SIN ASISTENCIA")
    ? "La cobertura obligatoria para circular. Cubre daños a terceros."
    : "La cobertura obligatoria más asistencia de grúa y auxilio mecánico.";
}

function rankCompleta(c) {
  const d = (c.descripcion || "").toUpperCase();
  if (c.tipo === "robo") return d.includes("PREMIUM") ? 4 : d.includes("SIN ASISTENCIA") ? 2 : 3;
  return d.includes("SIN ASISTENCIA") ? 0 : 1;
}

function comparacion(tipo) {
  if (tipo === "robo") {
    return {
      titulo: "Robo Clásico vs Robo Premium",
      intro: "Las dos cubren la Responsabilidad Civil obligatoria, el robo o hurto total y el incendio de tu moto. La diferencia está en el detalle:",
      colA: "Clásico", colB: "Premium",
      filas: [
        { label: "Franquicia por robo", a: "Con franquicia (3% del siniestro)", b: "SIN franquicia" },
        { label: "Reposición 0km", a: "No incluye", b: "Reposición a 0km si te la roban en los primeros 6 meses (requiere certificado de no rodamiento)", destacado: true },
        { label: "Daños parciales al amparo del robo", a: "No incluye", b: "Hasta 10% de la suma asegurada" },
      ],
    };
  }
  return {
    titulo: "RC Sin Asistencia vs RC Con Asistencia",
    intro: "Las dos son la cobertura obligatoria para circular: cubren daños a terceros hasta $208.000.000. La única diferencia es la asistencia:",
    colA: "Sin Asistencia", colB: "Con Asistencia",
    filas: [
      { label: "Grúa y auxilio mecánico (SOS)", a: "No incluye", b: "Hasta 6 servicios al año", destacado: true },
      { label: "Asistencia al viajero", a: "No incluye", b: "Incluida a partir de 100 km de tu domicilio" },
    ],
  };
}

export default function CoberturasStep({ cot, config, moto, onNext, onBack, onAplicarCupon, onQuitarCupon }) {
  const [sel, setSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [comparar, setComparar] = useState(null);
  const recomendada = cot.coberturas.reduce((a, b) => (a === null || rankCompleta(b) > rankCompleta(a) ? b : a), null);
  const recKey = recomendada ? `${recomendada.codigo}-${recomendada.plan_cot}` : null;
  const grupos = cot.coberturas.reduce((acc, c) => { (acc[c.tipo] = acc[c.tipo] || []).push(c); return acc; }, {});
  const comparables = Object.keys(grupos).filter((t) => grupos[t].length >= 2);
  return (
    <div data-testid="step-coberturas">
      <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">Elegi tu cobertura</h1>
      <p className="text-slate-500 mb-1">{moto.marca_desc} {moto.modelo_desc} {moto.anio}</p>
      {cot.datos_cotiz?.suma && parseFloat(cot.datos_cotiz.suma) > 0 && <p className="text-slate-400 text-sm mb-1">Suma asegurada de referencia: {fmtMoney(cot.datos_cotiz.suma)}</p>}
      <p className="text-sm text-slate-500 mb-4" data-testid="coberturas-instruccion">Tocá el botón <b className="text-emerald-700">Contratar</b> en la cobertura que quieras para avanzar.</p>
      {cot.cupon?.es_directo ? (
        <div className="rounded-[20px] border-2 border-emerald-300 bg-emerald-50 p-4 mb-4" data-testid="descuento-directo">
          <p className="font-heading font-bold text-emerald-900 flex items-center gap-1.5"><Ticket className="w-4 h-4" /> {cot.cupon.frase || "Esta cotización ya tiene un descuento por pagar con MercadoPago"}</p>
          <p className="text-sm text-emerald-800 mt-0.5">Precios con <b>{cot.cupon.bonificacion}% OFF</b> ya aplicado, sin cupón ni trámites.</p>
        </div>
      ) : onAplicarCupon ? (
        <CuponBox cupon={cot.cupon} onAplicar={onAplicarCupon} onQuitar={onQuitarCupon} />
      ) : null}
      <div className="space-y-4">
        {cot.coberturas.map((c, i) => {
          const cfg = config[c.codigo] || {};
          const nombre = cfg.nombre || c.descripcion;
          const desc = cfg.descripcion || descPorDefecto(c);
          const esRecomendada = `${c.codigo}-${c.plan_cot}` === recKey;
          const active = sel?.codigo === c.codigo && sel?.plan_cot === c.plan_cot;
          return (
            <div key={`${c.codigo}-${c.plan_cot}-${i}`} role="button" tabIndex={0}
              data-testid={`cobertura-card-${c.codigo}-${c.plan_cot}`}
              onClick={() => setSel({ ...c, nombre_visible: nombre })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSel({ ...c, nombre_visible: nombre }); }}
              className={`w-full text-left rounded-[24px] border-2 p-5 shadow-sm hover:shadow-lg transition cursor-pointer ${active ? "border-emerald-600 bg-emerald-50" : esRecomendada ? "border-[#8D1B3D]/60 bg-gradient-to-br from-white to-[#8D1B3D]/5" : "border-slate-200 bg-white"}`}>
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 mb-2">
                <div className="flex items-center gap-2">
                  {c.tipo === "robo" ? <ShieldAlert className="w-5 h-5 text-emerald-600 shrink-0" /> : <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />}
                  <p className="font-heading font-bold text-slate-900 text-lg leading-tight">{nombre}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <img src="/api/uploads/atm-logo.png" alt="ATM Seguros" className="h-5 w-auto" data-testid={`cobertura-atm-logo-${c.codigo}`} />
                  {esRecomendada && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-[#8D1B3D]/10 text-atm flex items-center gap-1" data-testid={`badge-recomendada-${c.codigo}`}>
                      <Star className="w-3 h-3 fill-[#8D1B3D] text-[#8D1B3D]" /> Recomendada
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${c.tipo === "robo" ? "bg-emerald-100 text-emerald-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {c.tipo === "robo" ? "Con robo" : "RC"}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-2">{desc}</p>
              <button type="button" data-testid={`cobertura-detalle-${c.codigo}`}
                onClick={(e) => { e.stopPropagation(); setDetalle({ nombre, texto: cfg.detalle || desc }); }}
                className="text-xs font-semibold text-emerald-700 underline underline-offset-2 mb-3">
                Que incluye esta cobertura
              </button>

              <div className="flex items-center gap-2">
                <div className="min-w-0">
                  {c.impcuotas_original && c.impcuotas_original !== c.impcuotas && (
                    <p className="text-sm text-slate-400 line-through leading-none" data-testid={`cobertura-precio-original-${c.codigo}-${c.plan_cot}`}>{fmtMoney(c.impcuotas_original)}</p>
                  )}
                  <p className="font-heading text-xl min-[400px]:text-2xl font-bold text-slate-900 whitespace-nowrap" data-testid={`cobertura-precio-${c.codigo}-${c.plan_cot}`}>{fmtMoney(c.impcuotas)}<span className="text-sm font-normal text-slate-500">/mes</span></p>
                  {c.impcuotas_original && c.impcuotas_original !== c.impcuotas && (
                    <p className="text-sm font-bold text-emerald-700 mt-0.5" data-testid={`cobertura-ahorro-${c.codigo}-${c.plan_cot}`}>Te ahorrás {fmtMoney(Number(c.impcuotas_original) - Number(c.impcuotas))}</p>
                  )}
                  <p className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
                    Pagás con
                    <span className="inline-flex items-center rounded bg-[#009EE3] px-1 py-0.5" title="Mercado Pago">
                      <img src="/assets/mp-logo.png" alt="Mercado Pago" className="h-3 w-auto" data-testid={`cobertura-mp-${c.codigo}-${c.plan_cot}`} />
                    </span>
                  </p>
                </div>
                <button type="button" data-testid={`cobertura-seleccionar-${c.codigo}-${c.plan_cot}`}
                  onClick={(e) => { e.stopPropagation(); onNext({ ...c, nombre_visible: nombre }); }}
                  className="ml-auto h-12 px-4 min-[400px]:px-5 rounded-[14px] bg-emerald-600 hover:bg-emerald-700 text-white font-heading font-bold text-base whitespace-nowrap transition shrink-0 flex items-center gap-1 shadow-[0_8px_20px_rgba(5,150,105,0.35)]">
                  Contratar <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5">
        <img src="/api/uploads/atm-logo.png" alt="" className="h-3 w-auto" />
        Coberturas y precios en vivo de ATM Seguros.
      </p>

      {comparables.map((tipo) => (
        <button key={tipo} type="button" data-testid={`comparar-${tipo}`} onClick={() => setComparar(tipo)}
          className="w-full mt-3 flex items-center justify-center gap-2 rounded-[20px] border-2 border-dashed border-emerald-300 bg-emerald-50/50 text-emerald-800 font-heading font-semibold text-sm py-3.5 hover:bg-emerald-50 transition">
          <GitCompare className="w-4 h-4" /> Ver diferencia entre las 2 opciones de {tipo === "robo" ? "Robo" : "Responsabilidad Civil"}
        </button>
      ))}

      {comparar && (() => {
        const c = comparacion(comparar);
        return (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setComparar(null)} data-testid="comparar-modal">
            <div className="bg-white rounded-t-[28px] sm:rounded-[24px] w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start gap-3 mb-2">
                <h3 className="font-heading font-bold text-lg text-slate-900">{c.titulo}</h3>
                <button data-testid="comparar-modal-close" onClick={() => setComparar(null)} className="p-2 -m-1 text-slate-400 shrink-0" aria-label="Cerrar"><X className="w-6 h-6" /></button>
              </div>
              <p className="text-sm text-slate-600 mb-4">{c.intro}</p>
              <div className="space-y-3">
                {c.filas.map((fila, i) => (
                  <div key={i} className={`rounded-xl p-3 border ${fila.destacado ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`comparar-fila-${i}`}>
                    <p className="font-heading font-semibold text-sm text-slate-900 mb-2 flex items-center gap-1.5">
                      {fila.destacado && <Star className="w-3.5 h-3.5 fill-emerald-600 text-emerald-600" />}{fila.label}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-white border border-slate-200 p-2">
                        <p className="font-semibold text-slate-400 uppercase tracking-wide text-[10px] mb-1">{c.colA}</p>
                        <p className="text-slate-600">{fila.a}</p>
                      </div>
                      <div className="rounded-lg bg-white border border-emerald-200 p-2">
                        <p className="font-semibold text-emerald-700 uppercase tracking-wide text-[10px] mb-1 flex items-center gap-1"><Check className="w-3 h-3" />{c.colB}</p>
                        <p className="text-slate-700 font-medium">{fila.b}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="comparar-modal-ok" onClick={() => setComparar(null)} className="w-full h-12 mt-5 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 text-white font-heading font-semibold">Entendido</button>
            </div>
          </div>
        );
      })()}
      {detalle && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setDetalle(null)} data-testid="cobertura-modal">
          <div className="bg-white rounded-t-[28px] sm:rounded-[24px] w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <h3 className="font-heading font-bold text-lg text-slate-900">{detalle.nombre}</h3>
              <button data-testid="cobertura-modal-close" onClick={() => setDetalle(null)} className="p-2 -m-1 text-slate-400 shrink-0" aria-label="Cerrar"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{detalle.texto}</p>
            <button data-testid="cobertura-modal-ok" onClick={() => setDetalle(null)} className="w-full h-12 mt-5 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 text-white font-heading font-semibold">Entendido</button>
          </div>
        </div>
      )}
      <button type="button" data-testid="wizard-back-button" onClick={onBack}
        className="mt-6 mb-6 text-sm text-slate-500 hover:text-emerald-700 transition">
        ← Atras
      </button>
    </div>
  );
}
